import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, Hidden, Modal } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';

import { ProductsReducerState } from '../../flux/reducers/product-reducer';
import { clearProductCatalog, fetchProductCatalog } from '../../flux/actions/product-actions';
import { getCategories, saveCategoryFilter } from '../../flux/actions/category-actions';
import { CategoryModel } from '../../models/category-model';
import CategoryFilterComponent from '../../components/Shop/CategoryFilterButton';
import ScreenLoader from '../../components/ScreenLoader';
import { ProductCard } from '../../components/ProductCard';
import { addToCart, resetMessage } from '../../flux/actions/shoppingCart-actions';
import SnackBarAlert from "../../components/Alerts/AlertComponent";

import shopStyles from './styles';

const Shop: FC = () => {
    const dispatch = useDispatch();
    const classes = shopStyles();
    const productReducer: ProductsReducerState = useSelector((state: any) => state.productReducer);
    const categoryReducer = useSelector((state: any) => state.categoryReducer);
    const rowsPerPage = 6;
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [fromPagination, setFromPagination] = useState(false);

    const { paginatedProducts, loading } = productReducer;
    const { categories, categoryFilter } = categoryReducer;
    const [isProductInCart, setIsProductInCart] = useState(false);
    const [isProductAdded, setIsProductAdded] = useState(false);
    const [showAlert, setShowAlert] = useState(false);

    const cart = useSelector((state: any) => state.cart);
    const { cartItems } = cart;
    /** Auxiliar category to generate the "Todas" filter */
    const allCategory: CategoryModel = {
        name: 'Todos',
        _id: '0000000000000000',
        description: 'all'
    };

    useEffect(() => {
        dispatch(clearProductCatalog());
        dispatch(getCategories());
    }, [dispatch]);

    // Effect to update categoryFilter by active categories in case they're not in applied filter
    useEffect(() => {
        const categoryIds = categories.map((category: CategoryModel) => category._id);

        const filtered = categoryFilter.filter((id: string) => {
            if (categoryIds.indexOf(id) > -1) {
                return id;
            }
        });

        dispatch(saveCategoryFilter(filtered));
    }, [categories]);

    useEffect(() => {
        if (fromPagination) {
            dispatch(fetchProductCatalog(page, rowsPerPage, categoryFilter));
        }
    }, [page, fromPagination]);

    useEffect(() => {
        setPage(1);
        setFromPagination(false);
        dispatch(fetchProductCatalog(1, rowsPerPage, categoryFilter));
    }, [categoryFilter]);

    const onCheckCategoryFilter = (_id: string, checked: boolean) => {
        if (checked) {
            dispatch(saveCategoryFilter([...categoryFilter, _id]));
        } else {
            const filtered = categoryFilter.filter((id: any) => id !== _id);
            dispatch(saveCategoryFilter(filtered));
        }
    }

    const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setIsProductAdded(false);
        setIsProductInCart(false);
        setShowAlert(false);
    }

    const onCheckAllCategoriesFilter = (event: any, checked: boolean) => {
        if (checked) {
            dispatch(saveCategoryFilter([]));
        }
    }

    const addToCartHandler = (id: string) => {
        let productInCart = cartItems.some((item:any) => item.id === id);
        if (!productInCart) {
            dispatch(addToCart(id, 1));
            setIsProductAdded(true);
        } else {
            setIsProductInCart(true);
        }
        setShowAlert(true);
    }

    const handleNextPage = (event: any, page: number) => {
        setPage(page);
        setFromPagination(true);
    }

    return (
        <Container>
            {loading && <ScreenLoader isOpen={loading}/>}
            <Grid alignItems="center" direction="row" justify="center" spacing={3} container>
                <Grid xs={12} item>
                    <h1 className={classes.title}>
                        Nuestros Productos
                    </h1>
                </Grid>
                <Hidden xsDown={true}>
                    <Grid xs={12} item className={classes.filtersContainer}>
                        <CategoryFilterComponent category={allCategory} onCheckFilter={onCheckAllCategoriesFilter} key={allCategory._id} checked={categoryFilter.length === 0}/>
                        {categories.map(
                            (category: CategoryModel) => 
                                <CategoryFilterComponent category={category} onCheckFilter={onCheckCategoryFilter} key={category._id} checked={categoryFilter.includes(category._id)}/>
                        )}
                    </Grid>
                </Hidden>
                <Hidden smUp={true}>
                    <Button 
                        variant="outlined"
                        onClick={() => setOpen(true)}
                        color="primary"
                        >
                        Categorías
                    </Button>
                    <Dialog
                        className={classes.categoriesModal}
                        open={open}
                        onClose={() => setOpen(false)}
                        >
                        <DialogTitle>
                            Categorias
                        </DialogTitle>
                        <DialogContent>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={categoryFilter.length === 0} onChange={onCheckAllCategoriesFilter}/>}
                                    label={allCategory.name}
                                />
                                {categories.map(
                                    (category: CategoryModel) => 
                                        <FormControlLabel
                                            key={category._id}
                                            control={<Checkbox checked={categoryFilter.includes(category._id)} onChange={(ev, ckd) => onCheckCategoryFilter(category._id, ckd)} />}
                                            label={category.name}
                                        />
                                )}
                            </FormGroup>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpen(false)} color="primary">
                                Aceptar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Hidden>
                {paginatedProducts?.products && paginatedProducts.products.map(
                    (product) => {
                        return (
                            <Grid sm={3} xs={12} key={product._id} item className={classes.productCard}> 
                                <ProductCard {...product} addToCartHandler={addToCartHandler} hideDescription/>
                            </Grid>
                        )
                    }
                )}
                {isProductInCart && <SnackBarAlert severity='info' message='Este producto ya fue agregado, modifica la cantidad en tu carrito!' open={isProductInCart} onClose={handleClose}/>}
                {isProductAdded && <SnackBarAlert severity='success' message='Se ha agregado un producto al carrito' open={isProductAdded} onClose={handleClose}/>}
                {paginatedProducts.products?.length === 0 && 
                    <Grid xs={12} item className={classes.noProduct}>
                        Disculpe las molestias, por el momento no hay producto disponible.
                    </Grid>}
                <Grid item xs={12}>
                    <Pagination
                        className={classes.pagination}
                        defaultPage={page}
                        color="secondary" 
                        count={paginatedProducts?.pages} 
                        page={page} 
                        onChange={handleNextPage} />
                </Grid>
            </Grid>
        </Container>
    )
}

export default Shop;
